import * as React from 'react';
import { Client, Plan, PatientStatus } from '../../../models/core';
import SectionFilter, { FilterField } from './SectionFilter';
import { EmailOutcomes, Languages, MailOutcomes, PatientStatuses, PhoneOutcomes, 
    RefusalReasons, ScheduledFailedReasons, TextOutcomes, VisitStatuses } from '../../../utils/enums';
import { Paper } from '@mui/material';

interface PatientFilterProps {
    client: Client;
    plans: Plan[];
    syncing:boolean;
    searchParams:Partial<Record<"page" | "sort" | FilterFields, string>>;
    userAssignmentFilters:string[];
    onChange:(searchFields:Partial<Record<any, string>>) => void;
}

export type FilterFields = 'search' | 'sort' | 'rgx.firstName' | 'rgx.lastName' | 'planId' 
    | 'visit.riskCategory' | 'rgx.county' | 'rgx.city' | 'zip'
    | 'visit.status'| 'visit.refusalReason'| 'key.phoneOutcome'| 'key.emailOutcome' 
    | 'key.textOutcome' | 'key.mailOutcome'
    | 'key.scheduledReason' | 'status' | 'key.outcomeMissing' | 'key.assignments';

const PatientFilterPanel = ({client, plans, syncing, searchParams, userAssignmentFilters, onChange}: PatientFilterProps): JSX.Element => {

    const filterValue = React.useMemo<{ [k in FilterFields]: string | string[] }>(() => ({
        search: searchParams.search || '',
        sort: searchParams.sort || '',
        'rgx.firstName': searchParams['rgx.firstName'] || '',
        'rgx.lastName': searchParams['rgx.lastName'] || '',
        planId: searchParams.planId?.split('|') || '',
        'rgx.city': searchParams['rgx.city'] || '',
        zip: searchParams.zip || '',
        'rgx.county': searchParams['rgx.county'] || '',
        'visit.riskCategory': searchParams['visit.riskCategory']?.split('|') || ['all'],
        'visit.status': searchParams['visit.status']?.split('|') || ['all'],
        'visit.refusalReason': searchParams['visit.refusalReason']?.split('|') || ['all'],
        'key.phoneOutcome': searchParams['key.phoneOutcome']?.split('|') || ['all'],
        'key.emailOutcome': searchParams['key.emailOutcome']?.split('|') || ['all'],
        'key.textOutcome': searchParams['key.textOutcome']?.split('|') || ['all'],
        'key.mailOutcome': searchParams['key.mailOutcome']?.split('|') || ['all'],
        'key.scheduledReason': searchParams['key.scheduledReason']?.split('|') || ['all'],
        'status' : !searchParams['status'] ? [PatientStatus.ACTIVE] : (searchParams['status']?.split('|') || ['all']),
        'key.outcomeMissing': searchParams['key.outcomeMissing']?.split('|') || ['all'],
        'key.assignments': searchParams['key.assignments']?.split('|') || ['all']
    }), [searchParams])


    const onFiltersChange = React.useCallback((value: { [k: string]: string | string[] }) => {
        function maybeJoin(x: string | string[]): string {
            return x == 'all' ? '' : (x instanceof Array) ? (x.includes('all') ? 'all' : x.join('|')) : x;
        }
        function rgxMaybeJoin(x: string | string[]): string {
            const rgxStr = x == 'all' ? '' : (x instanceof Array) ? (x.includes('all') ? 'all' : x.join('|')) : x;
            return rgxStr?.replaceAll('\\', '');
        }
        onChange({
            search: maybeJoin(value.search) || '',
            sort: maybeJoin(value.sort) || '',
            'rgx.firstName': rgxMaybeJoin(value['rgx.firstName']) || '',
            'rgx.lastName': rgxMaybeJoin(value['rgx.lastName']) || '',
            planId: plans.filter(p => (value.planId as string[]).includes(p.name)).map(p => p._id).join('|'),
            'rgx.city': rgxMaybeJoin(value['rgx.city']) || '',
            zip: maybeJoin(value.zip) || '',
            'visit.riskCategory': maybeJoin(value['visit.riskCategory']) || '',
            'rgx.county': rgxMaybeJoin(value['rgx.county']) || '',
            'visit.status': maybeJoin(value['visit.status']) || '',
            'visit.refusalReason': maybeJoin(value['visit.refusalReason']) || '',
            'key.phoneOutcome': maybeJoin(value['key.phoneOutcome']) || '',
            'key.emailOutcome': maybeJoin(value['key.emailOutcome']) || '',
            'key.textOutcome': maybeJoin(value['key.textOutcome']) || '',
            'key.mailOutcome': maybeJoin(value['key.mailOutcome']) || '',
            'key.scheduledReason': maybeJoin(value['key.scheduledReason']) || '',
            'status': maybeJoin(value['status']) || '',
            'key.outcomeMissing': maybeJoin(value['key.outcomeMissing']) || '',
            'key.assignments': maybeJoin(value['key.assignments']) || '',
            page:'1'
        });
    }, [plans, onChange]);

    const filterFields = React.useMemo<FilterField[]>(() => [
        { type: 'section', name:'top_bar', width:12, fields:[
                { name: 'search', type: 'search', placeholder: 'Insurance/Medicare/Medicaid ID', width: 11},
                { name: 'sort', type: 'enum', items:['Zip'], forceSingle:true, emptyLabel: "Name", width: 1 },
            ]
        },
        { type: 'section', name:'Patient', fields:[
                { name: 'rgx.firstName', type: 'string', placeholder: 'First Name', width: 6},
                { name: 'rgx.lastName', type: 'string', placeholder: 'Last Name', width: 6},
                { name: 'status', type: 'enum', items: PatientStatuses, emptyLabel: "Any Status", width: 6},
                { name: 'language', type: 'enum', items: Languages, emptyLabel: "Language", width: 6},
                { name: 'rgx.city', type: 'string', placeholder: 'City', width: 4},
                {
                    name: 'rgx.county', type: 'string', placeholder: 'County',
                    disabled: !client.layout.patientFields.find(f => f.field == 'county'), width: 4,
                },
                {name: 'zip', type: 'string', placeholder: 'ZIP', width: 4},
            ]
        },
        { type: 'section', name:'Client', fields:[
                {name: 'planId', type: 'enum', items: plans.map(p => p.name), emptyLabel: 'All Plans', width: 12},
            ]
        },
        { type: 'section', name:'Visit', fields:[
                {name: 'visit.riskCategory', type: 'enum', items: ['Top 1%', 'Top 2-5%', 'Top 6-20%', 'Bottom 80%'], emptyLabel: 'Any Risk', 
                    disabled: !client.layout.patientFields.find(f => f.field && f.field=='riskCategory'), width: 6},
                {name: 'visit.status', type: 'enum', items: VisitStatuses, emptyLabel: 'Any Status', width: 6},
            ]
        },
        { type: 'section', name:'Outreach', fields:[
                {name: 'key.phoneOutcome', type: 'enum', items: PhoneOutcomes, emptyLabel: 'Any Phone Outcome', width: 6},
                {name: 'key.emailOutcome', type: 'enum', items: EmailOutcomes, emptyLabel: 'Any Email Outcome', width: 6},
                {name: 'key.textOutcome', type: 'enum', items: TextOutcomes, emptyLabel: 'Any Text Outcome', width: 6},
                {name: 'key.mailOutcome', type: 'enum', items: MailOutcomes, emptyLabel: 'Any Mail Outcome', width: 6},
                {name: 'key.scheduledReason', type: 'enum', items: ScheduledFailedReasons, emptyLabel: 'Any Schedule Reason', width: 4},
                {name: 'key.outcomeMissing', type: 'enum', forceSingle:true, items: [
                    'No Phone 1st attempt', 'No Phone 2nd attempt', 'No Phone 3rd attempt',
                    'No Phone 4th attempt', 'No Phone 5th attempt', 'No Phone 6th attempt',
                    'No Email attempts', 'No Text attempts', 'No Mail attempts'
                ], emptyLabel: "Any Attempts", width: 4},
                {name: 'visit.refusalReason', type: 'enum', items: RefusalReasons, emptyLabel: 'Any Refusal Reason', width: 4},
            ]
        },
        { type: 'section', name:'Assignment', fields:[
            {name: 'key.assignments', type: 'enum', items: userAssignmentFilters ? [
                'Assigned',
                ...userAssignmentFilters.filter((f) => f !== 'None'),
                'None'
                ] : [], emptyLabel:'Any Assignment', 
                disabled:!(userAssignmentFilters?.length != 0), width: 12},
            ]
        }
    ], [client.layout.patientFields, plans, userAssignmentFilters]);


    return (<Paper sx={{marginBottom:'1em', pl:1, pr:1 }}><SectionFilter fields={filterFields} defaultValue={filterValue}
        isBusy={syncing} onChange={onFiltersChange} collapsable={true} gridPadding='2px 2px 8px 8px'/></Paper>)
}
export default PatientFilterPanel;